// Templates for Shadow DOM
const popupDialogTemplate = document.createElement("template");
const popupDialogTriggerTemplate = document.createElement("template");
const popupDialogContentTemplate = document.createElement("template");
const popupDialogHeaderTemplate = document.createElement("template");
const popupDialogTitleTemplate = document.createElement("template");
const popupDialogDescriptionTemplate = document.createElement("template");
const popupDialogFooterTemplate = document.createElement("template");
const popupDialogCloseTemplate = document.createElement("template");

// Template HTML
popupDialogTemplate.innerHTML = `
            <slot name="popup-dialog-trigger"></slot>
            <slot name="popup-dialog-content"></slot>
        `;

popupDialogTriggerTemplate.innerHTML = `<slot></slot>`;
popupDialogContentTemplate.innerHTML = `<slot></slot>`;
popupDialogHeaderTemplate.innerHTML = `<slot></slot>`;
popupDialogTitleTemplate.innerHTML = `<slot></slot>`;
popupDialogDescriptionTemplate.innerHTML = `<slot></slot>`;
popupDialogFooterTemplate.innerHTML = `<slot></slot>`;
popupDialogCloseTemplate.innerHTML = `<slot></slot>`;

// Main Popup Dialog
class PopupDialog extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      popupDialogTemplate.content.cloneNode(true)
    );

    this.isOpen = false;
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);

    // Create overlay element (like in your sheet example)
    // createElement("popup-overlay") creates an instance of the custom element, unlike createElement("template") which creates a blueprint for content.
    this.popupOverlay = document.createElement("popup-dialog-overlay");
    this.popupOverlay.setAttribute("role", "presentation");
    this.popupOverlay.setAttribute("data-state", "closed");
    this.popupOverlay.addEventListener("click", () => this.close());
  }

  connectedCallback() {
    this.popupTrigger = this.querySelector("popup-dialog-trigger");
    this.popupContent = this.querySelector("popup-dialog-content");
    this.popupCloseTrigger = this.querySelector(".popup-close-button");

    if (!this.popupCloseTrigger) {
      this._addCloseButton();
    } else {
      this.popupCloseTrigger.addEventListener("click", this.close.bind(this));
    }

    if (!this.popupTrigger || !this.popupContent) {
      console.warn(
        "<popup-dialog> needs both <popup-dialog-trigger> and <popup-dialog-content>"
      );
      return;
    }

    this.popupOverlay.remove();
    this.popupContent.remove();

    this._updateAriaExpanded();
    this.popupTrigger.addEventListener("click", this._onTriggerClick);

    // Add keyboard listener to document
    document.addEventListener("keydown", this._onKeyDown);
  }

  disconnectedCallback() {
    if (this.popupOverlay && this.popupOverlay.parentNode) {
      this.popupOverlay.remove();
    }
    if (this.popupContent && this.popupContent.parentNode) {
      this.popupContent.remove();
    }
    this.popupTrigger?.removeEventListener("click", this._onTriggerClick);
    document.removeEventListener("keydown", this._onKeyDown);
    if (this.popupCloseTrigger) {
      this.popupCloseTrigger.removeEventListener(
        "click",
        this.close.bind(this)
      );
    }
  }

  _addCloseButton() {
    const closeButton = document.createElement("button");
    closeButton.className = "popup-close-button";
    closeButton.setAttribute("aria-label", "Close dialog");
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    `;
    closeButton.addEventListener("click", this.close.bind(this));
    this.popupContent.appendChild(closeButton);
    this.popupCloseTrigger = closeButton;
  }

  _updateAriaExpanded() {
    if (this.popupTrigger) {
      this.popupTrigger.setAttribute("aria-expanded", String(this.isOpen));
    }
  }

  _onTriggerClick(e) {
    e.preventDefault();
    this.toggle();
  }

  _onKeyDown(e) {
    if (e.key === "Escape" && this.isOpen) {
      this.close();
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this._updateAriaExpanded();

    // Append the overlay to body (like your sheet example)
    document.body.appendChild(this.popupOverlay);

    // Move content to body (like your sheet example)
    if (this.popupContent.parentNode !== document.body) {
      document.body.appendChild(this.popupContent);
    }

    // Trigger reflow to ensure animations start properly after state changes
    this.popupOverlay.offsetHeight;
    this.popupContent.offsetHeight;

    // Update states to open, triggering CSS animations
    this.popupOverlay.setAttribute("data-state", "open");
    this.popupContent.setAttribute("data-state", "open");

    // Lock body scroll
    document.body.setAttribute("data-scroll-locked", "");

    // Focus management
    this.popupContent.setAttribute("tabindex", "-1");
    this.popupContent.focus();

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent("popup:open", {
        bubbles: true,
        detail: { dialog: this },
      })
    );
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this._updateAriaExpanded();

    // Close states
    this.popupOverlay.setAttribute("data-state", "closed");
    this.popupContent.setAttribute("data-state", "closed");

    // Cleanup after animation (like your sheet example)
    setTimeout(() => {
      if (this.popupOverlay.parentNode) {
        this.popupOverlay.remove();
      }

      if (this.popupContent.parentNode === document.body) {
        this.popupContent.remove();
      }

      document.body.removeAttribute("data-scroll-locked");
    }, 300);

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent("popup:close", {
        bubbles: true,
        detail: { dialog: this },
      })
    );
  }
}

// Dialog Trigger
class PopupDialogTrigger extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      popupDialogTriggerTemplate.content.cloneNode(true)
    );

    this.setAttribute("slot", "popup-dialog-trigger");
    this.setAttribute("role", "button");
    this.setAttribute("aria-haspopup", "dialog");
    this.setAttribute("tabindex", "0");
  }

  connectedCallback() {
    // Handle keyboard activation
    this.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.click();
      }
    });
  }
}

// Dialog Content
class PopupDialogContent extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      popupDialogContentTemplate.content.cloneNode(true)
    );

    this.setAttribute("slot", "popup-dialog-content");
    this.setAttribute("role", "dialog");
    this.setAttribute("aria-modal", "true");
    this.setAttribute("data-state", "closed");
  }
}

// Dialog Header
class PopupDialogHeader extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      popupDialogHeaderTemplate.content.cloneNode(true)
    );
  }
}

// Dialog Title
class PopupDialogTitle extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      popupDialogTitleTemplate.content.cloneNode(true)
    );
    this.setAttribute("role", "heading");
    this.setAttribute("aria-level", "2");
  }
}

// Dialog Description
class PopupDialogDescription extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      popupDialogDescriptionTemplate.content.cloneNode(true)
    );
  }
}

// Dialog Footer
class PopupDialogFooter extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      popupDialogFooterTemplate.content.cloneNode(true)
    );
  }
}

// Dialog Close
class PopupDialogClose extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      popupDialogCloseTemplate.content.cloneNode(true)
    );
    this.setAttribute("role", "button");
    this.setAttribute("tabindex", "0");
  }

  connectedCallback() {
    this.addEventListener("click", () => {
      const dialog =
        this.closest("popup-dialog") || document.querySelector("popup-dialog");
      if (dialog) {
        dialog.close();
      }
    });

    // Handle keyboard activation
    this.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.click();
      }
    });
  }
}

// Register all components

customElements.define("popup-dialog", PopupDialog);
customElements.define("popup-dialog-trigger", PopupDialogTrigger);
customElements.define("popup-dialog-content", PopupDialogContent);
customElements.define("popup-dialog-header", PopupDialogHeader);
customElements.define("popup-dialog-title", PopupDialogTitle);
customElements.define("popup-dialog-description", PopupDialogDescription);
customElements.define("popup-dialog-footer", PopupDialogFooter);
customElements.define("popup-dialog-close", PopupDialogClose);
